/**
 * 🗂 PORTFÖY — tüm sitelerin tek tabloda son bilinen durumu (ajans görünümü).
 * "Bugün hangi müşteriye bakmalıyım?" sorusunun ekranı: sağlık, otorite, takip
 * hareketi ve AI anılma tek satırda; satıra tıkla → o site aktif olur, Dashboard açılır.
 */
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { GlPageHeader, GlSection, GlEmpty } from "@/components/ui/gl-kit";
import { GlTableWrap, GlTable, GlTHead, GlTBody, GlTr, GlTh, GlTd, GlBadge } from "@/components/ui/gl-table";
import { SiteFavicon } from "@/components/site-favicon";
import { LayoutGrid, ArrowUp, ArrowDown, Plus } from "lucide-react";

type Row = {
  id: number; url: string; status: string; page_count: number;
  health: number | null; bl_rank: number | null; ref_domains: number | null;
  ai_score: number | null; tracked: number; avg_pos: string | number | null;
  improved: number; declined: number; lost: number;
  ai_prompts: number; ai_mention_rate: number | null;
  kesintide: boolean; okunmamis_olay: number;
};

const scoreColor = (v: number | null, goodAt = 80, warnAt = 50) =>
  v == null ? "light" : v >= goodAt ? "success" : v >= warnAt ? "warning" : ("error" as const);

export default function Portfolio() {
  const { data: domains } = useListDomains();
  const [, setActiveDomainId] = useActiveDomain(domains);
  const [, setLocation] = useLocation();

  const { data } = useQuery<{ portfolio: Row[] }>({
    queryKey: ["portfolio"],
    queryFn: async () => (await fetch("/api/portfolio")).json(),
  });
  const rows = data?.portfolio ?? [];

  const open = (id: number) => { setActiveDomainId(id); setLocation("/kokpit"); };

  return (
    <div className="mx-auto max-w-[1536px] p-4 md:p-6">
      <GlPageHeader
        title="Genel Bakış — Tüm Siteler"
        desc="Filonun son durumu tek tabloda. Bir siteye tıkla → o sitenin kokpiti açılır (tarama, durum, öncelikli işler)."
        icon={<LayoutGrid className="h-5 w-5" />}
        actions={
          <button onClick={() => setLocation("/domains/new")}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2.5 text-theme-sm font-medium text-white shadow-theme-xs hover:bg-brand-600">
            <Plus className="w-4 h-4" /> Yeni Site Taraması
          </button>
        }
      />
      <GlSection bodyClass="!px-0 !py-0">
        {!rows.length ? (
          <div className="p-6"><GlEmpty title="Henüz site yok" desc="Sağ üstteki 'Yeni Site Taraması' ile ilk siteni ekle — birkaç dakikada haritası çıkar." icon={<LayoutGrid className="h-6 w-6" />} /></div>
        ) : (
          <GlTableWrap>
            <GlTable>
              <GlTHead>
                <GlTr>
                  <GlTh>Site</GlTh>
                  <GlTh align="center">Nöbetçi</GlTh>
                  <GlTh align="right">Sayfa</GlTh>
                  <GlTh align="center">Sağlık</GlTh>
                  <GlTh align="center">Otorite</GlTh>
                  <GlTh align="right">Ref. Domain</GlTh>
                  <GlTh align="center">AI Görünürlük</GlTh>
                  <GlTh align="right">Takipte</GlTh>
                  <GlTh align="right">Ort. Sıra</GlTh>
                  <GlTh>Hareket</GlTh>
                  <GlTh align="center">AI Anılma</GlTh>
                </GlTr>
              </GlTHead>
              <GlTBody>
                {rows.map((r) => (
                  <GlTr key={r.id} onClick={() => open(r.id)}>
                    <GlTd>
                      <span className="inline-flex items-center gap-2 font-medium text-gray-800 dark:text-white/90">
                        <SiteFavicon url={r.url} size={16} />
                        {r.url.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                      </span>
                    </GlTd>
                    <GlTd align="center">
                      {r.kesintide
                        ? <GlBadge color="error">● kesinti</GlBadge>
                        : r.okunmamis_olay > 0
                          ? <GlBadge color="warning">{r.okunmamis_olay} olay</GlBadge>
                          : <GlBadge color="success">● nöbette</GlBadge>}
                    </GlTd>
                    <GlTd align="right"><span style={{ fontVariantNumeric: "tabular-nums" }}>{r.page_count}</span></GlTd>
                    <GlTd align="center">{r.health != null ? <GlBadge color={scoreColor(r.health)}>%{r.health}</GlBadge> : <span className="text-theme-xs text-gray-400">—</span>}</GlTd>
                    <GlTd align="center">{r.bl_rank != null ? <GlBadge color="light">{r.bl_rank}</GlBadge> : <span className="text-theme-xs text-gray-400">—</span>}</GlTd>
                    <GlTd align="right">{r.ref_domains ?? "—"}</GlTd>
                    <GlTd align="center">{r.ai_score != null ? <GlBadge color={scoreColor(r.ai_score, 40, 10)}>%{r.ai_score}</GlBadge> : <span className="text-theme-xs text-gray-400">—</span>}</GlTd>
                    <GlTd align="right">{r.tracked || "—"}</GlTd>
                    <GlTd align="right">{r.avg_pos != null ? Number(r.avg_pos) : "—"}</GlTd>
                    <GlTd>
                      {r.improved || r.declined || r.lost ? (
                        <span className="inline-flex items-center gap-1.5 text-theme-xs">
                          <span className="inline-flex items-center text-success-600"><ArrowUp className="h-3 w-3" />{r.improved}</span>
                          <span className="inline-flex items-center text-error-600"><ArrowDown className="h-3 w-3" />{r.declined}</span>
                          {!!r.lost && <GlBadge color="warning">{r.lost} kayıp</GlBadge>}
                        </span>
                      ) : <span className="text-theme-xs text-gray-400">—</span>}
                    </GlTd>
                    <GlTd align="center">{r.ai_mention_rate != null ? <GlBadge color={scoreColor(r.ai_mention_rate, 40, 10)}>%{r.ai_mention_rate}</GlBadge> : <span className="text-theme-xs text-gray-400">—</span>}</GlTd>
                  </GlTr>
                ))}
              </GlTBody>
            </GlTable>
          </GlTableWrap>
        )}
      </GlSection>
      <p className="mt-3 text-theme-xs text-gray-400">
        Değerler her sitenin son ajan koşusundan gelir; gece otomatik takibi açık olan sitelerde her sabah tazedir.
      </p>
    </div>
  );
}
